// «Структура → Таксономии» (R-C7, R-U27, decision 038 п.2, п.6): every taxonomy of the account as a table over the whole width — where it
// is used, how many values, an app's or own; its card with the values opens in the stack of modals. An own one goes to the archive or is
// deleted (a used one is not: the server names its fields); an app's one stays.

import { useState } from 'react';
import { taxonomyManaged, taxonomyRemoval, taxonomyUsage, uuidv7, type Id, type TaxonomyDef, type TaxonomyStyle } from '@confdeck/core-shared';
import { useMeta, useSync } from '../sync/react.tsx';
import { ModalStack, StackPane } from '../ui/ModalStack.tsx';
import { CompactTable } from '../table/CompactTable.tsx';
import { Button, Chip, ConfirmDialog, Dialog, Field, Input, SearchField, Segmented, useToast } from '../ui/primitives.tsx';
import { TermsEditor } from './TermsEditor.tsx';
import s from '../team/team.module.css';

const STYLE_LABEL: Record<TaxonomyStyle, string> = { labels: 'Метки', tree: 'Дерево', pipeline: 'Воронка' };
const FILTER = [{ value: 'all', label: 'Все' }, { value: 'own', label: 'Свои' }, { value: 'system', label: 'Из приложений' }, { value: 'archive', label: 'Архив' }] as const;
type Filter = (typeof FILTER)[number]['value'];

export function TaxonomiesScreen() {
  const meta = useMeta();
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState<Filter>('all');
  const [open, setOpen] = useState<Id[]>([]);
  const [creating, setCreating] = useState(false);
  const live = meta.taxonomies();
  const archived = meta.archived<TaxonomyDef>('taxonomy');
  const needle = q.trim().toLowerCase();
  const rows = (filter === 'archive' ? archived : live.filter((t) => filter === 'all' || (filter === 'system') === !!t.system))
    .filter((t) => !needle || t.name.toLowerCase().includes(needle))
    .sort((a, b) => Number(!!a.system) - Number(!!b.system) || a.name.localeCompare(b.name, 'ru'));
  const where = (t: TaxonomyDef) => taxonomyUsage(meta, t.id).filter((u) => !u.archived).map((u) => u.label).join('; ');
  const closeFrom = (i: number) => setOpen((cur) => cur.slice(0, i));
  return (
    <div className={s.screen}>
      <div className={s.bar}>
        <Segmented size="sm" label="Показать таксономии" value={filter} onChange={setFilter} items={FILTER.map((f) => ({ ...f, ...(f.value === 'archive' ? { label: `Архив${archived.length ? ` · ${archived.length}` : ''}` } : {}) }))} className={s.barFilter} />
        <SearchField size="sm" className={s.barSearch} value={q} onChange={setQ} label="Поиск по таксономиям" placeholder="Название" />
        <span className={s.spacer} />
        <span className={s.help}>{rows.length} из {filter === 'archive' ? archived.length : live.length}</span>
        <Button size="sm" variant="primary" icon="plus" onClick={() => setCreating(true)}>Таксономия</Button>
      </div>
      <div className={s.tableScroll}>
        {rows.length ? (
          <CompactTable className={s.wide} cardsOnPhone label="Таксономии" rows={rows} rowKey={(t) => t.id} onOpen={(t) => setOpen([t.id])} columns={[
            { key: 'name', label: 'Название', className: s.nameCol, render: (t) => <span className={s.cellName} title={t.name}>{t.name}</span> },
            { key: 'style', label: 'Вид', render: (t) => STYLE_LABEL[t.style] },
            { key: 'where', label: 'Где используется', render: (t) => { const w = where(t); return <span className={s.cellText} title={w}>{w || '—'}</span>; } },
            { key: 'terms', label: 'Значений', render: (t) => String(meta.terms(t.id).length) },
            { key: 'kind', label: 'Источник', render: (t) => (t.archivedAt ? <Chip color="gray">в архиве</Chip> : <Chip color={t.system ? 'gray' : 'blue'}>{t.system ? 'приложение' : 'своя'}</Chip>) },
          ]} />
        ) : (
          <div className={s.empty}>{needle || filter !== 'all' ? <>Ничего не нашли. <Button size="sm" variant="ghost" onClick={() => { setQ(''); setFilter('all'); }}>Сбросить</Button></> : 'Таксономий пока нет'}</div>
        )}
      </div>
      <ModalStack onClose={closeFrom} layers={open.map((id, i) => ({
        key: id, label: `Таксономия «${meta.taxonomy(id)?.name ?? ''}»`, size: 'card' as const,
        content: meta.taxonomy(id) ? <TaxonomyCard key={id} id={id} onClose={() => closeFrom(i)} /> : <div className={s.empty}>Таксономия удалена. <Button size="sm" variant="ghost" onClick={() => closeFrom(i)}>Закрыть</Button></div>,
      }))} />
      {creating && <Create onDone={(id) => { setCreating(false); if (id) { setFilter('all'); setOpen([id]); } }} />}
    </div>
  );
}

function Create({ onDone }: { onDone: (id: Id | null) => void }) {
  const client = useSync();
  const [name, setName] = useState('');
  const [style, setStyle] = useState<TaxonomyStyle>('labels');
  const [error, setError] = useState<string | null>(null);
  const make = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    const id = uuidv7();
    const r = await client.run('taxonomy.create', { id, name: name.trim(), style });
    if (r.ok) onDone(id); else setError(r.error.message);
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onDone(null)} label="Новая таксономия">
      <form onSubmit={make} className={s.dialog}>
        <h2 className={s.dialogTitle}>Новая таксономия</h2>
        <Field label="Название"><Input autoFocus placeholder="Например, «Источник лида»" value={name} onChange={(e) => setName(e.target.value)} /></Field>
        <Field label="Вид"><Segmented size="sm" label="Вид таксономии" value={style} onChange={setStyle} items={(['labels', 'tree', 'pipeline'] as const).map((v) => ({ value: v, label: STYLE_LABEL[v] }))} /></Field>
        <p className={s.help}>Значения добавите в её карточке, а поле «Метки или воронка» с ней — в настройке полей раздела.</p>
        {error && <p className={s.error} role="alert">{error}</p>}
        <div className={s.actions}><span className={s.spacer} /><Button onClick={() => onDone(null)}>Отмена</Button><Button type="submit" variant="primary" disabled={!name.trim()}>Создать</Button></div>
      </form>
    </Dialog>
  );
}

function TaxonomyCard({ id, onClose }: { id: Id; onClose: () => void }) {
  const meta = useMeta();
  const client = useSync();
  const toast = useToast();
  const t = meta.taxonomy(id);
  const [name, setName] = useState(t?.name ?? '');
  const [deleting, setDeleting] = useState(false);
  // «Влияние изменения»: the fields that show these values; renaming a value renames it everywhere they appear.
  const usage = taxonomyUsage(meta, id);
  const removal = taxonomyRemoval(meta, id);
  // Deleted while the card slides out: nothing to show.
  if (!t) return null;
  const rename = async () => {
    if (!name.trim() || name.trim() === t.name) { setName(t.name); return; }
    const r = await client.run('taxonomy.update', { id, name: name.trim() });
    toast(r.ok ? { kind: 'info', text: 'Название сохранено' } : { kind: 'error', text: r.error.message });
  };
  const run = async (command: string, text: string, close = false) => {
    const r = await client.run(command, { id });
    toast(r.ok ? { kind: 'info', text } : { kind: 'error', text: r.error.message });
    if (r.ok && close) onClose();
  };
  const about = !t.system ? 'своя таксономия' : taxonomyManaged(meta, id) ? 'из приложения: значения ставят его действия, их можно переименовать, но не убрать' : 'из приложения: значения можно переименовать, добавить и убрать';
  return (
    <StackPane onClose={onClose}
      title={<Input bare className={s.paneName} value={name} disabled={!!t.archivedAt} aria-label="Название таксономии" onChange={(e) => setName(e.target.value)} onBlur={() => void rename()} onKeyDown={(e) => { if (e.key === 'Enter') void rename(); }} />}
      sub={`${STYLE_LABEL[t.style]} · ${t.archivedAt ? 'в архиве: в новых полях не предлагается' : about}`}
      footer={t.system ? <span className={s.help}>Таксономия приложения: её нельзя убрать в архив или удалить — на ней работают его поля и действия.</span> : (
        <>
          {t.archivedAt
            ? <Button size="sm" variant="secondary" icon="undo" onClick={() => void run('taxonomy.restore', `«${t.name}» снова в списке`)}>Вернуть из архива</Button>
            : <Button size="sm" variant="secondary" icon="archive" onClick={() => void run('taxonomy.archive', `«${t.name}» в архиве. Поля с ней сохраняют значения`, true)}>В архив</Button>}
          <span className={s.spacer} />
          <Button size="sm" variant="danger" icon="trash" onClick={() => setDeleting(true)}>Удалить</Button>
        </>
      )}>
      <h3 className={s.h3First}>Где используется</h3>
      {usage.length
        ? <ul className={s.usage}>{usage.map((u) => <li key={u.fieldId}>{u.label}{u.archived ? <span className={s.rightHint}>{u.sectionArchived ? 'раздел в архиве' : 'поле в архиве'}</span> : null}</li>)}</ul>
        : <p className={s.help}>Пока не используется: добавьте поле «Метки или воронка» в нужный раздел и выберите эту таксономию.</p>}
      <h3 className={s.h3}>Значения</h3>
      <TermsEditor taxonomyId={id} canEdit={!t.archivedAt} />
      {/* The core's question (DESIGN.md «Настройки»): deletion is for good; a used taxonomy is offered the archive instead. */}
      {removal.delete ? (
        <ConfirmDialog open={deleting} title={`Удалить «${t.name}»?`} action="Удалить насовсем" danger onClose={() => setDeleting(false)}
          onConfirm={() => { setDeleting(false); void run('taxonomy.delete', `«${t.name}» удалена`, true); }}>
          Таксономия и её значения ({meta.terms(id).length}) удалятся насовсем: ни одно поле на неё не ссылается. Вернуть не получится — если она может понадобиться, уберите её в архив.
        </ConfirmDialog>
      ) : (
        <ConfirmDialog open={deleting} title={`«${t.name}» удалить нельзя`} action={t.archivedAt ? 'Понятно' : 'Убрать в архив'} onClose={() => setDeleting(false)}
          onConfirm={() => { setDeleting(false); if (!t.archivedAt) void run('taxonomy.archive', `«${t.name}» в архиве. Поля с ней сохраняют значения`, true); }}>
          <p className={s.help}>Таксономией пользуются поля, их записи хранят её значения:</p>
          <ul className={s.usage}>{removal.usedBy.map((u) => <li key={u}>{u}</li>)}</ul>
          {!t.archivedAt && <p className={s.help}>В архиве она пропадёт из выбора в новых полях, а значения в записях останутся.</p>}
        </ConfirmDialog>
      )}
    </StackPane>
  );
}
